import React, { useMemo, useRef } from "react";

import { Grid } from "@mui/material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import FieldView from "../../../../../../../components/Form/FieldView";
import { Job } from "../../../../../../../models/job";
import { ItemJob } from "../../../../../../../models/sales";
import { StorageBox } from "../../../../../../../models/storage.box";
import { groupStorageBoxOrders } from "../../../../../../../util/groupStorageBoxOrders";
import {
  OrderView,
  OrderViewComponentHandler,
} from "../../../Transshipments/components/OrderViewComponent";
import {
  EmptyValue,
  OrderLink,
  OrderReference,
  OrderRoot,
  OrderUploadDate,
  OrderWrapper,
  OrdersLabel,
} from "./styled";
import { getCurrentLocationStorageBox } from "../../../../../../../util/getCurrentLocationStorageBox";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";
import { useAppState } from "Context/StateContext";

interface StorageBoxDetailsProps {
  data: StorageBox;
}

export default function StorageBoxDetails(props: StorageBoxDetailsProps) {
  const { data } = props;
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const orderViewComponentRef = useRef<OrderViewComponentHandler>(null);

  const getLocationName = useMemo(() => {
    return getCurrentLocationStorageBox(data, translate);
  }, [data]);

  let jobs =
    data.itemsJobs?.reduce((jobs: Job[], itemJob: ItemJob) => {
      if (itemJob.job) jobs.push(itemJob.job);

      return jobs;
    }, []) || [];

  if (data.jobs) {
    jobs = [...jobs, ...data.jobs];
  }

  const orders = groupStorageBoxOrders(jobs);

  return (
    <Grid container direction="row" spacing={2}>
      <Grid item container xs={12} spacing={3}>
        <Grid item xs={4}>
          <FieldView label={translate("number")} value={data.number} data-cy="storage-box-number" />
        </Grid>
        <Grid item xs={4}>
          <FieldView
            label={translate("type")}
            value={data.storageBoxType?.name}
            data-cy="storage-box-type"
          />
        </Grid>
        <Grid item xs={4}>
          <FieldView
            label={translate("location")}
            value={getLocationName}
            data-cy="storage-box-location"
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={3}>
        <Grid item xs={4}>
          <FieldView
            label={translate("status")}
            value={data.storageStatus?.name}
            data-cy="storage-status"
          />
        </Grid>
        <Grid item xs={4}>
          <FieldView
            label={translate("updated")}
            value={
              data?.updatedAt && currentUser?.preferences
                ? formatDateWithUserPref(data?.updatedAt, currentUser.preferences)
                : ""
            }
            data-cy="storage-status"
          />
        </Grid>
      </Grid>
      <Grid item container direction="column" xs={12}>
        <OrdersLabel>{translate("orders")}</OrdersLabel>
        <OrderWrapper>
          {!orders.length && <EmptyValue>{translate("empty")}</EmptyValue>}
          {orders.map((job) => (
            <OrderRoot key={`item-job-${job.id}`}>
              <div>
                <OrderReference>
                  <OrderLink
                    onClick={() => {
                      orderViewComponentRef?.current?.open(Number(job.id));
                    }}
                  >
                    {job?.reference}
                  </OrderLink>{" "}
                  - {job?.customer?.fullName}
                </OrderReference>
              </div>
              <div>
                <OrderUploadDate>
                  <b>{translate("uploadDate")}: </b>
                  <span>
                    {job?.updatedAt && currentUser?.preferences
                      ? formatDateWithUserPref(job?.updatedAt, currentUser.preferences, "dateOnly")
                      : ""}
                  </span>
                </OrderUploadDate>
              </div>
            </OrderRoot>
          ))}
        </OrderWrapper>
      </Grid>
      <OrderView ref={orderViewComponentRef} />
    </Grid>
  );
}
